import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { navigationForRole } from '@/utils/navigation';
import { useTaskBadge } from '@/hooks/useTaskBadge';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isAvailable, setIsAvailable] = useState(user?.is_available ?? true);
  const [togglingAvailability, setTogglingAvailability] = useState(false);
  const [switchingRole, setSwitchingRole] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const taskCounts = useTaskBadge(user, location.pathname);
  const taskCount = taskCounts.due || 0;

  // Dual-role: OH who is also a counselor can flip between modes. We only enable this
  // when `also_counselor` is true on the user record.
  const isDualRole = !!user?.also_counselor;
  const activeMode = user?.role; // backend already swaps role to effective active_role
  const otherMode = activeMode === 'counselor' ? 'operating_head' : 'counselor';
  const handleSwitchRole = async () => {
    if (!isDualRole || switchingRole) return;
    setSwitchingRole(true);
    try {
      const r = await apiClient.post('/auth/switch-role', { role: otherMode });
      const newToken = r?.data?.access_token;
      if (newToken) {
        localStorage.setItem('token', newToken);
      }
      // Hard reload so every cached store / RBAC gate re-evaluates with the new role.
      window.location.reload();
    } catch (e) {
      setSwitchingRole(false);
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || 'Failed to switch role');
    }
  };
  
  // Sync availability with user object
  React.useEffect(() => {
    if (user?.is_available !== undefined) {
      setIsAvailable(user.is_available);
    }
  }, [user]);

  // Safety net: Radix menus/dialogs set `pointer-events:none` on <body> while
  // open and clean it up on close. If one unmounts mid-close during a route
  // change (e.g. tapping a notification that navigates), the lock can linger and
  // freeze the whole app. Clear it on every navigation so the UI never gets stuck.
  useEffect(() => {
    document.body.style.pointerEvents = '';
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)');
    const closeDrawer = () => { if (desktop.matches) setMobileSidebarOpen(false); };
    desktop.addEventListener('change', closeDrawer);
    return () => desktop.removeEventListener('change', closeDrawer);
  }, []);

  // Fetch notifications for all users (admin, operating_head, counselor, team_leader)
  useEffect(() => {
    const fetchNotifications = async () => {
      // All roles can receive notifications
      if (user?.role) {
        try {
          const response = await apiClient.get('/notifications?limit=15');
          setNotifications(response.data.notifications || []);
          setUnreadCount(response.data.unread_count || 0);
        } catch (error) {
          console.error('Failed to fetch notifications:', error);
        }
      }
    };
    
    fetchNotifications();
    // Poll for new notifications every 20 seconds for faster updates
    const interval = setInterval(fetchNotifications, 20000);
    return () => clearInterval(interval);
  }, [user]);

  const markNotificationAsRead = async (notificationId) => {
    try {
      await apiClient.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const snoozeFollowup = async (studentId, minutes, notificationId, e) => {
    if (e) e.stopPropagation();
    try {
      await apiClient.post(`/students/${studentId}/snooze-followup`, { minutes });
      toast.success(`Snoozed ${minutes} min — reminder re-armed`);
      if (notificationId) markNotificationAsRead(notificationId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to snooze task');
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.put('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const filteredNavigation = navigationForRole(user?.role, location.pathname);

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'admin': 'Administrator',
      'accountant': 'Accountant',
      'operating_head': 'Operating Head',
      'counselor': 'Counselor',
      'documentation_team': 'Documentation Team',
      'telecaller': 'Telecaller',
      'team_leader': 'Team Leader'
    };
    return roleMap[role] || role;
  };

  const getRoleColor = (role) => {
    const colorMap = {
      'admin': 'bg-red-100 text-red-800',
      'operating_head': 'bg-purple-100 text-purple-800',
      'accountant': 'bg-green-100 text-green-800',
      'counselor': 'bg-blue-100 text-blue-800',
      'documentation_team': 'bg-amber-100 text-amber-800'
    };
    return colorMap[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div data-testid="app-sidebar" className={`sidebar ${mobileSidebarOpen ? 'open' : ''} fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out 
        ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${sidebarOpen ? 'lg:translate-x-0' : 'lg:-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-20 px-4 border-b border-white border-opacity-10">
            <div className="flex items-center">
              <img 
                src="/patricks-logo.png" 
                alt="Patrick's Edu Care" 
                className="h-14 w-auto object-contain brightness-0 invert"
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileSidebarOpen(false)}
              aria-label="Close navigation"
              data-testid="mobile-sidebar-close"
              className="lg:hidden text-white hover:bg-white hover:bg-opacity-10"
            >
              <i className="fas fa-times"></i>
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 min-h-0 overflow-y-auto px-4 py-6 space-y-2" data-testid="sidebar-navigation">
            {filteredNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setMobileSidebarOpen(false)}
                className={`sidebar-item flex items-center px-4 py-3 text-sm font-medium transition-all duration-200 group ${
                  item.current
                    ? 'active text-white'
                    : 'text-white text-opacity-80 hover:text-white'
                }`}
                data-testid={`nav-${item.id}`}
                aria-current={item.current ? 'page' : undefined}
              >
                <i className={`${item.icon} mr-3 text-lg`}></i>
                {item.name}
                {item.href === '/tasks' && taskCount > 0 && (
                  <span className={`ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full ${taskCounts.overdue ? 'bg-red-500' : 'bg-amber-500'} text-white text-[10px] font-bold`} data-testid="sidebar-tasks-badge" title={`${taskCount} today + overdue · ${taskCounts.open} total open`}>
                    {taskCount > 99 ? '99+' : taskCount}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-white border-opacity-10">
            <div className="flex items-center">
              <Avatar className="h-10 w-10 ring-2 ring-white ring-opacity-20">
                {user?.profile_picture && (
                  <AvatarImage src={user.profile_picture} alt={user.full_name} />
                )}
                <AvatarFallback className="bg-teal-500 text-white">
                  {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-white truncate" data-testid="sidebar-user-name">
                  {user?.full_name}
                </p>
                <span data-testid="sidebar-user-role" className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-teal-500 bg-opacity-20 text-teal-200 border border-teal-400 border-opacity-30">
                  {getRoleDisplayName(user?.role)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div data-testid="app-main-content" className={`min-w-0 transition-[margin-left] duration-0 lg:duration-300 ease-in-out ${
        sidebarOpen ? 'lg:ml-64' : 'lg:ml-0'
      }`}>
        {/* Top Navigation */}
        <header className="staff-header header-premium bg-white border-b border-gray-100 sticky top-0 z-30" data-testid="staff-header">
          <div className="flex items-center justify-between gap-2 h-14 sm:h-16 px-3 sm:px-6">
            <div className="flex items-center min-w-0">
              {/* Mobile Hamburger Menu */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
                className="mr-2 lg:hidden text-gray-600 hover:bg-gray-100 p-2"
                data-testid="mobile-menu-button"
                aria-label="Open navigation"
                aria-expanded={mobileSidebarOpen}
              >
                <i className="fas fa-bars text-lg"></i>
              </Button>
              
              {/* Desktop Sidebar Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="hidden lg:block mr-4 text-gray-600 hover:bg-gray-100"
                data-testid="sidebar-toggle"
                aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              >
                <i className={`fas ${sidebarOpen ? 'fa-times' : 'fa-bars'}`}></i>
              </Button>
              
              {/* Logo/Title - visible on mobile */}
              <div className="staff-mobile-brand flex items-center min-w-0 lg:hidden">
                <img 
                  src="/patricks-logo.png" 
                  alt="Patrick's Edu Care" 
                  className="h-7 sm:h-8 w-auto object-contain"
                />
              </div>
              
              <div className="ml-4 hidden lg:block">
                <h1 className="text-xl font-semibold text-gray-900">
                  CRM Dashboard
                </h1>
              </div>
            </div>

            <div className="staff-header-actions flex items-center gap-1 sm:gap-4 shrink-0">
              {/* Counselor Availability Toggle - Mobile Optimized */}
              {user?.role === 'counselor' && (
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-gray-600 hidden sm:inline">Availability:</span>
                  <button
                    onClick={async () => {
                      setTogglingAvailability(true);
                      try {
                        const response = await apiClient.put(`/users/${user.id}/availability`, {
                          is_available: !isAvailable
                        });
                        setIsAvailable(!isAvailable);
                      } catch (error) {
                        console.error('Failed to toggle availability:', error);
                      } finally {
                        setTogglingAvailability(false);
                      }
                    }}
                    disabled={togglingAvailability}
                    data-testid="counsellor-availability-toggle"
                    aria-label="Counsellor availability"
                    role="switch"
                    aria-checked={isAvailable}
                    title={isAvailable ? 'Available for assignment' : 'Unavailable for assignment'}
                    className="staff-availability relative inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                  >
                    <span className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${isAvailable ? 'bg-green-500' : 'bg-gray-300'}`}><span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${isAvailable ? 'translate-x-5' : 'translate-x-1'}`} /></span>
                  </button>
                </div>
              )}

              {/* Notification Bell - All Users */}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="relative p-2 hover:bg-gray-100"
                    data-testid="notification-bell"
                  >
                    <i className="fas fa-bell text-gray-600 text-lg"></i>
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium animate-pulse">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    <span>Notifications</span>
                    {unreadCount > 0 && (
                      <button 
                        onClick={markAllAsRead}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        Mark all read
                      </button>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      <i className="fas fa-bell-slash text-2xl mb-2 text-gray-300"></i>
                      <p>No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <DropdownMenuItem
                        key={notification.id}
                        data-testid={`notification-item-${notification.id}`}
                        className={`flex flex-col items-start gap-1 p-3 cursor-pointer ${
                          !notification.is_read ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => {
                          markNotificationAsRead(notification.id);
                          // Smart deep-link: take the user straight to the exact
                          // lead/student the notification is about. IMPORTANT: defer
                          // the navigation so the Radix dropdown finishes its
                          // close/cleanup first — navigating synchronously unmounts
                          // the menu mid-close and can leave `pointer-events:none` on
                          // <body>, freezing the whole app ("stuck").
                          const sid = notification.student_id;
                          const leadTypes = ['lead_assignment', 'lead_reassignment', 'follow_up_reminder'];
                          let dest = notification.url?.startsWith('/') && !notification.url.startsWith('//') ? notification.url : null;
                          if (dest) {
                            // Server supplies preference-category-aware links (e.g. own incentive ledger).
                          } else if (sid) {
                            dest = `/students/${sid}`;            // exact lead/student profile
                          } else if (leadTypes.includes(notification.notification_type)) {
                            dest = '/leads';                       // fallback when no id is attached
                          }
                          if (dest) {
                            setTimeout(() => navigate(dest), 60);
                          }
                        }}
                      >
                        <div className="flex items-center gap-2 w-full">
                          {/* Icon based on notification type */}
                          <span className="flex-shrink-0">
                            {notification.notification_type === 'lead_assignment' && <i className="fas fa-user-plus text-green-500"></i>}
                            {notification.notification_type === 'lead_reassignment' && <i className="fas fa-exchange-alt text-blue-500"></i>}
                            {notification.notification_type === 'follow_up_reminder' && <i className="fas fa-clock text-orange-500"></i>}
                            {notification.notification_type === 'availability_alert' && <i className="fas fa-user-slash text-red-500"></i>}
                            {notification.notification_type === 'payment_received' && <i className="fas fa-rupee-sign text-green-500"></i>}
                            {!['lead_assignment', 'lead_reassignment', 'follow_up_reminder', 'availability_alert', 'payment_received'].includes(notification.notification_type) && 
                              <i className="fas fa-bell text-gray-500"></i>}
                          </span>
                          <span className={`text-sm font-medium flex-1 ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                            {notification.title}
                          </span>
                          {!notification.is_read && (
                            <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0"></span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 line-clamp-2 ml-5">{notification.message}</p>
                        {notification.notification_type === 'follow_up_reminder' && notification.student_id && (
                          <div className="flex items-center gap-1.5 ml-5 mt-1.5" data-testid={`notif-snooze-${notification.id}`} onClick={(e) => e.stopPropagation()}>
                            <span className="text-[10px] text-gray-400 mr-0.5">Snooze:</span>
                            {[15, 30, 60].map((m) => (
                              <button
                                key={m}
                                type="button"
                                onClick={(e) => snoozeFollowup(notification.student_id, m, notification.id, e)}
                                className="px-2 py-0.5 rounded-full border border-orange-200 bg-orange-50 text-[11px] font-medium text-orange-700 hover:bg-orange-100"
                                data-testid={`notif-snooze-${notification.id}-${m}`}
                              >
                                +{m}m
                              </button>
                            ))}
                          </div>
                        )}
                        <span className="text-xs text-gray-400 ml-5">
                          {(() => {
                            let raw = notification.created_at || '';
                            if (typeof raw === 'string') {
                              raw = raw.replace(/Z$/, '+05:30');
                              if (!raw.includes('+') && !raw.includes('-', 11)) raw += '+05:30';
                            }
                            return new Date(raw).toLocaleString('en-IN', { 
                              timeZone: 'Asia/Kolkata',
                              dateStyle: 'short',
                              timeStyle: 'short'
                            });
                          })()}
                        </span>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* User Menu - Mobile Optimized */}
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-1 sm:gap-2 hover:bg-gray-50 px-2 sm:px-3" data-testid="user-menu-trigger">
                  <Avatar className="h-7 w-7 sm:h-8 sm:w-8">
                    {user?.profile_picture && (
                      <AvatarImage src={user.profile_picture} alt={user.full_name} />
                    )}
                    <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs sm:text-sm">
                      {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs sm:text-sm font-medium text-gray-700 hidden sm:inline truncate max-w-[100px] lg:max-w-none">
                    {user?.full_name}
                  </span>
                  {isDualRole && (
                    <span className={`hidden md:inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                      activeMode === 'counselor' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                  : 'bg-indigo-50 text-indigo-700 border border-indigo-200'}`}
                          data-testid="active-role-badge">
                      <i className={`fas fa-${activeMode === 'counselor' ? 'headset' : 'user-tie'} mr-1`}></i>
                      {activeMode === 'counselor' ? 'COUNSELOR' : 'OH'}
                    </span>
                  )}
                  <i className="fas fa-chevron-down text-xs text-gray-400 hidden sm:inline"></i>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.full_name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => navigate('/profile')}
                  className="cursor-pointer" 
                  data-testid="profile-menu-item"
                >
                  <i className="fas fa-user mr-2"></i>
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => navigate('/settings')}
                  className="cursor-pointer" 
                  data-testid="settings-menu-item"
                >
                  <i className="fas fa-cog mr-2"></i>
                  Settings
                </DropdownMenuItem>
                {isDualRole && (
                  <>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1.5">
                      <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Active Role</div>
                      <div className="flex items-center justify-between gap-2 px-1">
                        <span className="text-xs font-medium text-gray-700">
                          {activeMode === 'counselor' ? (
                            <><i className="fas fa-headset mr-1.5 text-emerald-600"></i>Counselor</>
                          ) : (
                            <><i className="fas fa-user-tie mr-1.5 text-indigo-600"></i>Operating Head</>
                          )}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleSwitchRole}
                        disabled={switchingRole}
                        className="mt-2 w-full text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded px-2 py-1.5 transition-colors disabled:opacity-50"
                        data-testid="switch-role-btn"
                      >
                        {switchingRole ? (
                          <><i className="fas fa-spinner fa-spin mr-1"></i> Switching…</>
                        ) : (
                          <><i className="fas fa-exchange-alt mr-1"></i> Switch to {otherMode === 'counselor' ? 'Counselor' : 'Operating Head'}</>
                        )}
                      </button>
                    </div>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={logout}
                  className="cursor-pointer text-red-600 focus:text-red-600"
                  data-testid="logout-menu-item"
                >
                  <i className="fas fa-sign-out-alt mr-2"></i>
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="staff-page-content p-3 sm:p-6 pb-24 lg:pb-6" data-testid="staff-page-content">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation Bar — one-tap access to primary sections on phones */}
      <nav
        className="staff-bottom-nav fixed bottom-0 inset-x-0 z-40 lg:hidden bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]"
        data-testid="mobile-bottom-nav"
        aria-label="Primary navigation"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="grid" style={{ gridTemplateColumns: `repeat(${Math.min(filteredNavigation.length, 4) + 1}, minmax(0, 1fr))` }}>
          {filteredNavigation.slice(0, 4).map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`relative flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${
                item.current ? 'text-teal-600' : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`bottomnav-${item.id}`}
              aria-current={item.current ? 'page' : undefined}
            >
              <span className="relative">
                <i className={`${item.icon} text-lg`}></i>
                {item.href === '/tasks' && taskCount > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none" data-testid="bottomnav-tasks-badge">
                    {taskCount > 99 ? '99+' : taskCount}
                  </span>
                )}
              </span>
              <span className="truncate max-w-[64px]">{item.name}</span>
            </Link>
          ))}
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium text-gray-500 hover:text-gray-700 transition-colors"
            data-testid="bottomnav-more"
          >
            <i className="fas fa-ellipsis-h text-lg"></i>
            <span>More</span>
          </button>
        </div>
      </nav>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setMobileSidebarOpen(false)}
          data-testid="mobile-overlay"
        ></div>
      )}
    </div>
  );
};

export default Layout;